import { FEEDS } from '#/shared/constants';
import { parseGtfsId } from '#/shared/utils/gtfs-id';

/** routeIds can include an appended letter, such as 293a, representing future service changes
 This regex gets the numeric portion, which we need in order to query the data.
 *
 * @param {string|number} routeId ID string or number to be parsed
 * @returns {string}
*/
export const parseNumericalPortionOfRouteId = routeId => {
  return String(routeId).match(/\d+/)?.[0];
};

/**
 * For non-TriMet legs, replace headsign with agency name and route long name
 * so that external agency info is visible in contexts (like print) where
 * headsign is the only label shown.
 *
 * @param {object} leg OTP itinerary leg
 * @returns {string|undefined}
 */
export const resolveHeadsign = leg => {
  const feedId = parseGtfsId(leg?.routeId)?.feedId;
  if (feedId === FEEDS.TRIMET) {
    return leg.headsign;
  }
  return `${leg.agencyName} - ${leg.routeLongName}`;
};

/**
 * For legs, we delete the leg.trip.tripHeadsign value if it exists
 * We do this because otp-ui prefers leg.trip.tripHeadsign over leg.headsign when present,
 * bypassing the desired result that resolveHeadsign provides for non-Trimet legs.
 * @returns {object} leg - with trip.tripHeadsign deleted
 */
export const removeTripHeadsign = leg => {
  if (leg?.trip?.tripHeadsign === undefined) return leg;
  const trip = { ...leg.trip };
  delete trip.tripHeadsign;
  return { ...leg, trip };
};

/** return a list of block ids seen in the itinerary */
export const itineraryToBlockIds = itinerary => {
  return itinerary.legs
    .filter(leg => leg.trip?.blockId)
    .map(leg => {
      const { gtfsId, blockId } = leg.trip;
      const gtfsIdPrefix = parseGtfsId(gtfsId)?.feedId ?? '';
      return `${gtfsIdPrefix}:${blockId}`;
    });
};

/**
 * Normalizes provider URLs from GTFS-derived API data, which may be
 * inconsistently formatted across agencies (e.g., missing protocol).
 *
 * @param {string | undefined | null} rawUrl
 * @returns {string | undefined | null}
 */
export const normalizeProviderUrl = rawUrl => {
  if (!rawUrl) return rawUrl;

  const url = rawUrl.trim();
  return url.includes('://') ? url : `https://${url}`;
};

/**
 * Determines whether at least one leg on the supplied itinerary uses a TriMet
 * transit service
 *
 * @param {import('@opentripplanner/types').Itinerary} itinerary
 * @returns {boolean}
 */
export const itineraryUsesTriMet = itinerary =>
  !!itinerary?.legs?.some(
    leg => leg.transitLeg && leg.agencyId?.startsWith(`${FEEDS.TRIMET}:`)
  );

/**
 * Determines if the supplied itinerary legs have a least one transit leg that
 * is operated by a non-TriMet provider
 *
 * @param {import('@opentripplanner/types').Leg[] | null | undefined} legs
 * @returns {boolean} true if any leg is a transit leg from a non-TriMet provider
 */
export const legsUseThirdPartyTransit = legs =>
  !!legs?.some(
    leg =>
      leg.transitLeg &&
      leg.agencyId &&
      !leg.agencyId.startsWith(`${FEEDS.TRIMET}:`)
  );
